'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { usePathname, useRouter, useSearchParams } from '@/lib/router';
import { track } from '@/lib/telemetry';
import { usePeriodSelection } from '@/hooks/usePeriodSelection';
import type { PeriodPreset } from '@/lib/period-presets';
import type { PaymentMethodFilter, PaymentType, RecurrenceFilter, RefundFacet, SortKey, SortOrder, TransactionDirection } from '@/lib/transactions/types';
import { PLATFORM_KEYS, type PlatformFilter, type PlatformKey } from '@/lib/transactions/platform';
import { VALID_DIRECTIONS, sanitizeRecurrence } from './transactionType';

export type CategoryFilterMode = 'parent' | 'exact';

export interface ScreenState {
  q: string;
  categoryIds: string[];
  categoryFilterMode: CategoryFilterMode;
  account: string | null;
  paymentMethod: PaymentMethodFilter;
  /** "Canal de compra" (VER-358) — single-select purchase channel. */
  platform: PlatformFilter;
  direction: TransactionDirection | null;
  recurrence: RecurrenceFilter[];
  /** Tag membership (VER-378) — multi-select, OR-unioned by the API. */
  tagIds: string[];
  refund: RefundFacet;
  /** Read-only here — written by the period picker in the filter aside (VER-260), via usePeriodSelection. */
  period: PeriodPreset;
  fromParam: string | null;
  toParam: string | null;
  sort: SortKey;
  order: SortOrder;
  dateRange: { from: Date; to: Date };
  /** Stub list — telemetry-driven recents land in M5 (per VER-80 acceptance). */
  recentCategoryIds: readonly string[];
  patch: (updates: Partial<{
    q: string | null;
    categoryIds: string[] | null;
    categoryFilterMode: CategoryFilterMode | null;
    account: string | null;
    paymentMethod: PaymentMethodFilter;
    platform: PlatformFilter;
    direction: TransactionDirection | null;
    recurrence: RecurrenceFilter[] | null;
    tagIds: string[] | null;
    refund: RefundFacet;
    sort: SortKey;
    order: SortOrder;
  }>) => void;
}

// Filter-usage telemetry: every transactions filter flows through `patch`, so
// this is the single capture point. `set` fires on every (re)apply; `clear`
// only when the filter was actually active — the "Limpar filtros" button
// patches all keys at once and must not emit no-op clears. Search tracks only
// the empty↔non-empty transitions (never per keystroke, never the text).
const TRACKED_FILTERS: Record<string, { filter: string; withValue: boolean }> = {
  categoryIds: { filter: 'category', withValue: false },
  account: { filter: 'account', withValue: false },
  paymentMethod: { filter: 'payment_method', withValue: true },
  platform: { filter: 'platform', withValue: true },
  direction: { filter: 'direction', withValue: true },
  recurrence: { filter: 'recurrence', withValue: true },
  // Never emit tag ids as the value — same policy as categories.
  tagIds: { filter: 'tag', withValue: false },
  refund: { filter: 'refund', withValue: true },
  sort: { filter: 'sort', withValue: true },
  order: { filter: 'sort_order', withValue: true },
};

// State-key → URL-param aliases (where they differ). Single source of truth for both the telemetry
// diff and the URL patch, so the two never drift.
const URL_PARAM_ALIAS: Record<string, string> = {
  paymentMethod: 'pmt',
  categoryIds: 'cat',
  recurrence: 'rec',
  direction: 'dir',
  tagIds: 'tag',
};

function urlParamFor(key: string): string {
  return URL_PARAM_ALIAS[key] ?? key;
}

function trackFilterPatch(updates: Record<string, unknown>, previous: URLSearchParams): void {
  Object.entries(updates).forEach(([key, value]) => {
    const mapping = TRACKED_FILTERS[key];
    if (!mapping) return;
    const cleared = value == null || value === '' || (Array.isArray(value) && value.length === 0);
    const paramName = urlParamFor(key);
    if (cleared && previous.get(paramName) == null) return;
    track('ui.filter_applied', {
      surface: 'transactions',
      filter: mapping.filter,
      action: cleared ? 'clear' : 'set',
      ...(mapping.withValue && !cleared ? { value: String(value) } : {}),
    });
  });
}

const VALID_SORT: ReadonlySet<string> = new Set(['date', 'amount', 'merchant']);
const VALID_ORDER: ReadonlySet<string> = new Set(['asc', 'desc']);
const VALID_PAYMENT: ReadonlySet<string> = new Set(['debit', 'credit', 'pix', 'transfer', 'boleto', 'other']);
const VALID_REFUND: ReadonlySet<string> = new Set(['confirmed', 'pending']);
const VALID_PLATFORM: ReadonlySet<string> = new Set(PLATFORM_KEYS);

const RECENTS_STUB: readonly string[] = ['delivery', 'mercado', 'transporte', 'streaming', 'compras'] as const;

/** Per-key default sort direction. Matches conventional finance UX: */
const DEFAULT_ORDER_BY_SORT: Record<SortKey, SortOrder> = {
  date: 'desc',
  amount: 'desc',
  merchant: 'asc',
};

// Read a URL param, keep it only if it's in the allow-list, else null. Callers `?? fallback` when a
// non-null default is wanted. One place for the "trust the URL only if valid" rule every facet uses.
function pickParam<T extends string>(raw: string | null, valid: ReadonlySet<string>): T | null {
  return raw && valid.has(raw) ? (raw as T) : null;
}

// categoryIds carries the extra catMode coupling (clearing the list clears the mode), so it gets its
// own writer — the multi-value + coupling is the one branch heavy enough to warrant extraction.
// Every patch value is one of these — never an arbitrary object — so String() below is safe.
type PatchValue = string | readonly string[] | null | undefined;

function applyCategoryIdsParam(params: URLSearchParams, value: PatchValue): void {
  params.delete('cat');
  if (!Array.isArray(value)) {
    params.delete('catMode');
    return;
  }
  value.forEach((v) => params.append('cat', v));
  if (value.length === 0) params.delete('catMode');
}

// Write one filter key's value into the URL params (inverse of the parse above). Kept out of the
// patch callback so that stays a thin per-key dispatch. Multi-value keys (cat/rec) reset then append.
function applyUrlParam(params: URLSearchParams, key: string, value: PatchValue): void {
  if (key === 'categoryIds') {
    applyCategoryIdsParam(params, value);
    return;
  }
  if (key === 'categoryFilterMode') {
    if (value === 'exact') params.set('catMode', 'exact');
    else params.delete('catMode');
    return;
  }
  if (key === 'recurrence') {
    params.delete('rec');
    if (Array.isArray(value)) value.forEach((v) => params.append('rec', v));
    return;
  }
  if (key === 'tagIds') {
    params.delete('tag');
    if (Array.isArray(value)) value.forEach((v) => params.append('tag', v));
    return;
  }
  const paramName = urlParamFor(key);
  if (value === null || value === '' || value === undefined) params.delete(paramName);
  else params.set(paramName, String(value));
}

export function useTransactionsScreenState(): ScreenState {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { value: periodSelection } = usePeriodSelection();

  const q = searchParams.get('q') ?? '';

  const categoryIdsKey = searchParams.getAll('cat').join(',');
  const categoryIds = useMemo(
    () => (categoryIdsKey ? categoryIdsKey.split(',').filter(Boolean) : []),
    [categoryIdsKey],
  );
  const categoryModeRaw = searchParams.get('catMode');
  const categoryFilterMode: CategoryFilterMode =
    categoryIds.length > 0 && categoryModeRaw === 'exact' ? 'exact' : 'parent';

  const accountRaw = searchParams.get('account');
  const account = accountRaw && accountRaw !== 'all' ? accountRaw : null;

  const paymentMethod: PaymentMethodFilter = pickParam<PaymentType>(
    searchParams.get('pmt'),
    VALID_PAYMENT,
  );

  const platform: PlatformFilter = pickParam<PlatformKey>(
    searchParams.get('platform'),
    VALID_PLATFORM,
  );

  const direction = pickParam<TransactionDirection>(searchParams.get('dir'), VALID_DIRECTIONS);

  const recurrenceKey = searchParams.getAll('rec').join(',');
  const recurrence = useMemo(
    () => sanitizeRecurrence(recurrenceKey ? recurrenceKey.split(',') : []),
    [recurrenceKey],
  );

  const tagIdsKey = searchParams.getAll('tag').join(',');
  const tagIds = useMemo(
    () => (tagIdsKey ? tagIdsKey.split(',').filter(Boolean) : []),
    [tagIdsKey],
  );

  const refund = pickParam<Exclude<RefundFacet, null>>(searchParams.get('refund'), VALID_REFUND);

  const sort: SortKey = pickParam<SortKey>(searchParams.get('sort'), VALID_SORT) ?? 'date';

  const order: SortOrder =
    pickParam<SortOrder>(searchParams.get('order'), VALID_ORDER) ?? DEFAULT_ORDER_BY_SORT[sort];

  // Stabilise the dateRange object reference across renders. Without this, the
  // literal `{ from, to }` below is a new object every render and any
  // `useEffect([dateRange, ...])` consumer refetches on every render — which
  // is exactly the loop we just hit on the cockpit endpoint.
  const dateRangeFromMs = periodSelection.from.getTime();
  const dateRangeToMs = periodSelection.to.getTime();
  const dateRange = useMemo(
    () => ({ from: periodSelection.from, to: periodSelection.to }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateRangeFromMs, dateRangeToMs],
  );

  // Search telemetry needs a synchronous "is a search active?" flag: rapid
  // keystrokes outrun the router URL update, so diffing against searchParams
  // would re-fire 'set' once per keystroke.
  const searchActiveRef = useRef(q.trim() !== '');
  // Resync when q changes outside patch (browser back/forward between searched
  // and unsearched URLs) so the next interaction isn't suppressed or doubled.
  useEffect(() => {
    searchActiveRef.current = q.trim() !== '';
  }, [q]);

  const patch: ScreenState['patch'] = useCallback(
    (updates) => {
      const params = new URLSearchParams(searchParams.toString());
      Object.entries(updates).forEach(([key, value]) => applyUrlParam(params, key, value));
      if ('q' in updates) {
        // Trimmed like the effective query — whitespace-only is not a search.
        const hasQuery = typeof updates.q === 'string' && updates.q.trim() !== '';
        if (hasQuery !== searchActiveRef.current) {
          searchActiveRef.current = hasQuery;
          track('ui.filter_applied', {
            surface: 'transactions',
            filter: 'search',
            action: hasQuery ? 'set' : 'clear',
            method: 'search',
          });
        }
      }
      trackFilterPatch(updates, searchParams);
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [router, pathname, searchParams],
  );

  return {
    q,
    categoryIds,
    categoryFilterMode,
    account,
    paymentMethod,
    platform,
    direction,
    recurrence,
    tagIds,
    refund,
    period: periodSelection.preset,
    fromParam: periodSelection.preset === 'custom'
      ? periodSelection.from.toISOString().slice(0, 10)
      : null,
    toParam: periodSelection.preset === 'custom'
      ? periodSelection.to.toISOString().slice(0, 10)
      : null,
    sort,
    order,
    dateRange,
    recentCategoryIds: RECENTS_STUB,
    patch,
  };
}
